/**
 * Plain-English label mapping
 *
 * Maps technical config terms to user-friendly labels. UI-only —
 * the underlying JSON still uses technical names (no backend changes).
 */

export const ATTRIBUTE_OPTIONS = [
  { value: 'innerText', label: 'Text content' },
  { value: 'value', label: 'Input value' },
  { value: 'textContent', label: 'Text content (all)' },
  { value: 'innerHTML', label: 'HTML content' },
];

/**
 * Get the plain-English label for a DOM attribute.
 * Falls back to "Data attribute" for data-* attributes,
 * or the raw value if unknown.
 */
export function attributeLabel(attr) {
  const match = ATTRIBUTE_OPTIONS.find(o => o.value === attr);
  if (match) return match.label;
  if (attr && attr.startsWith('data-')) return `Data: ${attr}`;
  return attr || 'Input value';
}

/**
 * Check if an attribute value is a standard option or custom (data-* etc).
 */
export function isCustomAttribute(attr) {
  return !ATTRIBUTE_OPTIONS.some(o => o.value === attr);
}

export const CONDITION_MODE_LABELS = {
  allow_when_true: 'Only capture when…',
  suppress_when_true: "Don't capture when…",
};

export const SECTION_HELP = {
  process: {
    title: 'What is a process?',
    content: 'A process is a workflow you want to track — like "Invoice Approval" or "Employee Onboarding". Each process has multiple steps that users go through.'
  },
  steps: {
    title: 'What are steps?',
    content: 'Steps are the individual actions in your workflow. Each step happens on a specific web page. For example: "Invoice Submitted" happens on your ERP at erp.acme.com/invoices/*.'
  },
  triggers: {
    title: 'What is a trigger?',
    content: 'A trigger tells the extension which button or element to watch. When a user clicks it, the step is captured. Use your browser\'s Inspect tool (right-click → Inspect) to find the CSS selector.'
  },
  tracking: {
    title: 'What are tracking values?',
    content: 'For each step, tell us which value on the page identifies what\'s being tracked — like an invoice number, order ID, or ticket number. You\'ll use these later when creating Reports to define how steps connect into a workflow.'
  },
  trackingPerStep: 'Each step can have different tracking values — use whatever identifying data is available on that page. If the same value (like an invoice number) appears across multiple steps, you\'ll be able to use it later in Reports to connect them.',
  conditions: {
    title: 'What are conditions?',
    content: 'Conditions let you control when a step is captured. For example: "Only capture when the status field says Pending" or "Don\'t capture when the amount is empty". All enabled conditions must pass for the rule to apply.'
  },
  customFields: {
    title: 'What are custom fields?',
    content: 'Custom fields let you extract extra data from the page and attach it to the event — like a department name, dollar amount, or priority level. They don\'t affect tracking, just add metadata.'
  },
  debug: {
    title: 'What is debug mode?',
    content: 'When enabled on a step, conditions are overridden (events are always captured) and each event includes a DOM snapshot for troubleshooting. Turn this off before going live.'
  }
};
